import React, { FocusEvent, forwardRef, KeyboardEvent, useState } from 'react';
import cx from 'classnames';
import { Textbox, TextBoxProps } from '../Textbox';
import { blue, gray } from '../Color';
import { codes } from '../../data/translations';
import { useTranslationAPI } from '../../modules/translations';
import classes from './styles/index.module.scss';
import { Search } from '../Icons/Search';

export type SearchTextboxProps = {
	onSearch?: (value?: string | number) => void;
} & TextBoxProps;

export const SearchTextbox = forwardRef<HTMLInputElement, SearchTextboxProps>((props, ref) => {
	const { onSearch, ...textboxProps } = props;

	const { translate } = useTranslationAPI();

	const [focused, setFocused] = useState(false);

	const handleFocus = (event: FocusEvent<HTMLInputElement>) => {
		setFocused(true);
		textboxProps.onFocus?.(event);
	};

	const handleBlur = (event: FocusEvent<HTMLInputElement>) => {
		setFocused(false);
		textboxProps.onBlur?.(event);
	};

	const handleSearch = () => {
		if (textboxProps.disabled) {
			return;
		}

		setFocused(false);
		onSearch?.(textboxProps.value);
	};

	const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
		textboxProps.onKeyDown?.(event);

		if (event.key === 'Enter') {
			onSearch?.(textboxProps.value);
		}
	};

	const wrapperClasses = cx(classes.searchTextboxWrapper, {
		[classes.searchTextboxWrapper_focused]: focused,
		[classes.searchTextboxWrapper_disabled]: textboxProps.disabled,
	});

	return (
		<div className={wrapperClasses}>
			<Textbox
				{...textboxProps}
				ref={ref}
				onFocus={handleFocus}
				onBlur={handleBlur}
				onKeyDown={handleKeyDown}
				inputClassName={classes.searchInput}
				placeholder={textboxProps.placeholder || translate(codes.common.search)}
			/>
			<button
				type='button'
				onClick={handleSearch}
				aria-label='apply search'
				className={classes.searchBtn}
				disabled={textboxProps.disabled}
			>
				<Search fill={focused ? blue[7] : gray[6]} />
			</button>
		</div>
	);
});

SearchTextbox.displayName = 'SearchTextbox';
